import { useEffect, useState } from "react";
import { motion, useScroll, useTransform } from "framer-motion";
import { useNavigate } from "react-router-dom";
import { ArrowRight, ChevronDown, Copy } from "lucide-react";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { ParticleField } from "@/components/ParticleField";

const MaskedLine = ({ children, delay = 0, className = "" }) => (
  <span className={`block overflow-hidden ${className}`}>
    <motion.span
      className="block"
      initial={{ y: "115%" }}
      animate={{ y: 0 }}
      transition={{ duration: 1, delay, ease: [0.16, 1, 0.3, 1] }}
    >
      {children}
    </motion.span>
  </span>
);

const copyIp = (value, label) => {
  const done = () => toast.success(`${label} copied`, { description: value });
  if (navigator.clipboard?.writeText) {
    navigator.clipboard.writeText(value).then(done).catch(done);
  } else {
    done();
  }
};

export const Hero = () => {
  const navigate = useNavigate();
  const { scrollY } = useScroll();
  const y = useTransform(scrollY, [0, 700], [0, 160]);
  const opacity = useTransform(scrollY, [0, 500], [1, 0]);
  const [status, setStatus] = useState(null);

  useEffect(() => {
    let timer;
    const load = () => api.get("/server/status").then((r) => setStatus(r.data)).catch(() => {});
    load();
    timer = setInterval(load, 60000);
    return () => clearInterval(timer);
  }, []);

  return (
    <section data-testid="hero-section" className="relative min-h-screen flex items-center justify-center overflow-hidden">
      <div className="absolute inset-0 bg-[#05030A]" />
      <div className="absolute inset-0" style={{ background: "radial-gradient(ellipse 60% 45% at 50% 42%, rgba(139,92,246,0.22), transparent 70%)" }} />
      <ParticleField className="absolute inset-0" />
      <div className="absolute inset-0 bg-gradient-to-b from-transparent via-transparent to-[#05030A]" />

      <motion.div style={{ y, opacity }} className="relative z-10 text-center px-6 pt-24 pb-16 max-w-4xl mx-auto">
        <MaskedLine delay={0.1}>
          <span data-testid="hero-overline" className="inline-flex items-center gap-4 text-[11px] font-semibold tracking-[0.35em] text-[#A78BFA]">
            <span className="w-10 h-px bg-[#8B5CF6]/50" />
            SURVIVAL MINECRAFT
            <span className="w-10 h-px bg-[#8B5CF6]/50" />
          </span>
        </MaskedLine>

        <h1 data-testid="hero-wordmark" className="mt-6 select-none">
          <motion.span
            className="block font-display text-glow font-black text-6xl sm:text-8xl lg:text-[9rem] leading-none tracking-tight bg-gradient-to-br from-[#EDE9FE] via-[#A78BFA] to-[#7C3AED] bg-clip-text text-transparent"
            initial={{ opacity: 0, y: 40, filter: "blur(12px)" }}
            animate={{ opacity: 1, y: 0, filter: "blur(0px)" }}
            transition={{ duration: 1.1, delay: 0.25, ease: [0.16, 1, 0.3, 1] }}
          >
            ULTISMP
          </motion.span>
        </h1>

        <div data-testid="hero-tagline" className="mt-8 text-slate-400 text-base sm:text-lg leading-relaxed">
          <MaskedLine delay={0.45}>A private, high-stakes survival realm with custom coded</MaskedLine>
          <MaskedLine delay={0.55}>plugins. Break limits, build your legacy, and become</MaskedLine>
          <MaskedLine delay={0.65}>unstoppable.</MaskedLine>
        </div>

        <motion.div
          className="mt-12 flex flex-col sm:flex-row items-center justify-center gap-4"
          initial={{ opacity: 0, y: 24 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, delay: 0.9, ease: [0.16, 1, 0.3, 1] }}
        >
          <button
            onClick={() => navigate("/store")}
            data-testid="hero-enter-store-button"
            className="group inline-flex items-center gap-2 px-8 py-4 rounded-xl bg-[#8B5CF6] hover:bg-[#7C3AED] text-white text-sm font-bold tracking-[0.15em] shadow-[0_0_40px_rgba(139,92,246,0.45)] hover:shadow-[0_0_60px_rgba(139,92,246,0.65)] transition-[background-color,box-shadow,transform] duration-300 hover:-translate-y-0.5"
          >
            ENTER THE STORE
            <ArrowRight className="w-4 h-4 transition-transform duration-300 group-hover:translate-x-1" />
          </button>
          <button
            onClick={() => copyIp("ULTIsmp.net", "Server IP")}
            data-testid="hero-copy-ip-button"
            className="inline-flex items-center gap-2 px-8 py-4 rounded-xl bg-white/5 hover:bg-white/10 border border-white/10 hover:border-[#8B5CF6]/50 text-white text-sm font-bold tracking-[0.15em] transition-[background-color,border-color,transform] duration-300 hover:-translate-y-0.5"
          >
            <Copy className="w-4 h-4" />
            GET SERVER IP
          </button>
        </motion.div>

        <motion.div
          className="mt-8 flex justify-center"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ duration: 1, delay: 1.05 }}
        >
          {status?.online ? (
            <div data-testid="hero-player-count" className="inline-flex items-center gap-2.5 px-4 py-1.5 rounded-full border border-emerald-400/30 bg-emerald-400/10 text-emerald-300 text-xs font-bold tracking-[0.15em]">
              <span className="relative flex h-2 w-2">
                <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-emerald-400 opacity-75" />
                <span className="relative inline-flex rounded-full h-2 w-2 bg-emerald-400" />
              </span>
              {status.players_online} PLAYING NOW
            </div>
          ) : status ? (
            <div data-testid="hero-player-count" className="inline-flex items-center gap-2.5 px-4 py-1.5 rounded-full border border-white/10 bg-white/5 text-slate-500 text-xs font-bold tracking-[0.15em]">
              <span className="h-2 w-2 rounded-full bg-slate-600" />
              SERVER OFFLINE
            </div>
          ) : null}
        </motion.div>

        <motion.div
          className="mt-6 flex flex-col sm:flex-row items-center justify-center gap-3 sm:gap-8 text-sm"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ duration: 1, delay: 1.2 }}
        >
          <button
            onClick={() => copyIp("ULTIsmp.net", "Java IP")}
            data-testid="hero-java-ip-chip"
            className="group flex items-center gap-3 text-slate-500 hover:text-slate-200 transition-colors duration-300"
          >
            <span className="text-xs tracking-[0.2em]">JAVA IP</span>
            <span className="font-mono text-slate-300 group-hover:text-[#A78BFA] transition-colors duration-300">ULTIsmp.net</span>
          </button>
          <button
            onClick={() => copyIp("ULTIsmp.net:19132", "Bedrock IP")}
            data-testid="hero-bedrock-ip-chip"
            className="group flex items-center gap-3 text-slate-500 hover:text-slate-200 transition-colors duration-300"
          >
            <span className="text-xs tracking-[0.2em]">BEDROCK IP</span>
            <span className="font-mono text-slate-300 group-hover:text-[#A78BFA] transition-colors duration-300">ULTIsmp.net:19132</span>
          </button>
        </motion.div>
      </motion.div>

      <motion.div
        className="absolute bottom-8 left-1/2 -translate-x-1/2 text-slate-600"
        animate={{ y: [0, 8, 0] }}
        transition={{ duration: 2, repeat: Infinity, ease: "easeInOut" }}
      >
        <ChevronDown className="w-5 h-5" />
      </motion.div>
    </section>
  );
};
